// @ts-nocheck
import {
  computed,
  getCurrentInstance,
  inject,
  ref,
  toRaw,
  unref,
  watch,
} from 'vue'
import { get, isEmpty, isNil } from 'lodash-unified'
import { throttle } from 'lodash'
import {
  isObject as _isObject,
  escapeStringRegexp,
} from '@code-monkey-ui/utils'
import { dshSelectGroupKey, dshSelectKey } from './token'

import type { Ref } from 'vue'
import type { DshQueryChangeCtx } from './token'
import type ElCheckbox from '@code-monkey-ui/components/checkbox'

export function useOption(props, states) {
  // inject
  const select = inject(dshSelectKey)
  const selectGroup = inject(dshSelectGroupKey, { disabled: false })

  // template refs
  const checkBoxRef = ref<InstanceType<typeof ElCheckbox> | null>(null)
  // computed
  const isObject = computed(() => _isObject(props.value))

  const itemSelected = computed(() => {
    if (!select.props.multiple) {
      return isEqual(props.value, select.props.modelValue)
    } else {
      return contains(select.props.modelValue as unknown[], props.value)
    }
  })

  const limitReached = computed(() => {
    if (select.props.multiple) {
      const modelValue = (select.props.modelValue || []) as unknown[]
      return (
        !itemSelected.value &&
        modelValue.length >= select.props.multipleLimit &&
        select.props.multipleLimit > 0
      )
    } else {
      return false
    }
  })

  const currentLabel = computed(() => {
    return props.label || (isObject.value ? '' : props.value)
  })

  const currentValue = computed(() => {
    return isEmpty(props.value) && isNil(props.value)
      ? props.label
      : props.value
  })

  const currentTip = computed(() => {
    return props.tip || ''
  })

  const onlyThis = computed(() => {
    return select.props.onlyThis || true
  })

  const isDisabled = computed(() => {
    return props.disabled || states.groupDisabled || limitReached.value
  })

  const instance = getCurrentInstance()

  const contains = (arr = [], target) => {
    if (!isObject.value) {
      return arr && arr.includes(target)
    } else {
      const valueKey = select.props.valueKey
      return (
        arr &&
        arr.some((item) => {
          return toRaw(get(item, valueKey)) === get(target, valueKey)
        })
      )
    }
  }

  const isEqual = (a: unknown, b: unknown) => {
    if (!isObject.value) {
      return a === b
    } else {
      const { valueKey } = select.props
      return get(a, valueKey) === get(b, valueKey)
    }
  }

  const hoverItem = () => {
    if (!props.disabled && !selectGroup.disabled) {
      select.hoverIndex = select.optionsArray.indexOf(instance.proxy)
    }
  }

  watch(
    () => currentLabel.value,
    () => {
      if (!props.created && !select.props.remote) select.setSelected()
    }
  )

  watch(
    () => props.value,
    (val, oldVal) => {
      const { remote, valueKey } = select.props

      if (!Object.is(val, oldVal)) {
        select.onOptionDestroy(oldVal, instance.proxy)
        select.onOptionCreate(instance.proxy)
      }

      if (!props.created && !remote) {
        if (
          valueKey &&
          _isObject(val) &&
          _isObject(oldVal) &&
          val[valueKey] === oldVal[valueKey]
        ) {
          return
        }
        select.setSelected()
      }
    }
  )

  watch(
    () => selectGroup.disabled,
    () => {
      states.groupDisabled = selectGroup.disabled
    },
    { immediate: true }
  )

  const { queryChange } = toRaw(select)

  const changeQuery = (changes: string) => {
    const { query } = unref(changes)
    const regexp = new RegExp(escapeStringRegexp(query), 'i')
    states.visible =
      props.value === 'select-all' ||
      props.value === 'reverse' ||
      props.value === 'select-hidden' ||
      regexp.test(currentLabel.value + currentTip.value) ||
      props.created
    if (
      !states.visible &&
      props.value !== 'select-all' &&
      props.value !== 'reverse' &&
      props.value !== 'select-hidden'
    ) {
      select.filteredOptionsCount--
    }
    select.showFilterHiddenNode(changes)
  }
  watch(
    queryChange,
    (change) => {
      throttle(changeQuery, 300)(change)
    },
    { immediate: true }
  )

  return {
    select,
    currentLabel,
    currentTip,
    currentValue,
    itemSelected,
    isDisabled,
    hoverItem,
    checkBoxRef,
    onlyThis,
  }
}
